/**
 * QuizTimer (Sprint 6 B4) — countdown header.
 *
 * Props:
 *   - deadline: ISO datetime string (server-authoritative)
 *   - onExpire: callback gọi khi time hết (auto-submit)
 *
 * Display: `HH:MM:SS` (hours hidden khi 0). Red khi < 5 phút.
 * Update mỗi giây. Cleanup interval khi unmount.
 */

import { useEffect, useState } from "react";
import { Tag } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";

interface Props {
  deadline: string; // ISO
  onExpire?: () => void;
}

function format(secondsLeft: number): string {
  if (secondsLeft <= 0) return "00:00";
  const h = Math.floor(secondsLeft / 3600);
  const m = Math.floor((secondsLeft % 3600) / 60);
  const s = secondsLeft % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

export function QuizTimer({ deadline, onExpire }: Props) {
  const deadlineMs = new Date(deadline).getTime();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const secondsLeft = Math.max(0, Math.floor((deadlineMs - now) / 1000));
  const isCritical = secondsLeft > 0 && secondsLeft < 300; // < 5 phút
  const isExpired = secondsLeft === 0;

  useEffect(() => {
    if (isExpired && onExpire) {
      onExpire();
    }
  }, [isExpired, onExpire]);

  return (
    <Tag
      color={isExpired ? "red" : isCritical ? "volcano" : "blue"}
      icon={<ClockCircleOutlined />}
      style={{ fontSize: 16, padding: "4px 12px" }}
      data-testid="quiz-timer"
    >
      {format(secondsLeft)}
    </Tag>
  );
}
